import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useRouter } from 'src/routes/hooks';
import { useAuthContext } from 'src/auth/hooks';
import { varHover } from 'src/components/animate';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { paths } from 'src/routes/paths';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Role } from 'src/models/user';
import { getFileById } from '../../api/upload';
import { useOrganizationContext } from 'src/hooks/use-organization-context';
import { useNavigate } from 'react-router';
import { landingUrl } from 'src/config-global';


export default function AccountPopover() {

  const { currentOrg, handleOrganizationSwitchAndExit } = useOrganizationContext();

  const router = useRouter();

  const { user } = useAuthContext();

  const { logout } = useAuthContext();

  const popover = usePopover();

  const navigate = useNavigate();

  const [proPicture, setProPicture] = useState<string>();


  const handleLogout = async () => {
    try {
      await logout();
      handleOrganizationSwitchAndExit(null);
      popover.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  const exitOrganizationMode = useCallback(() => {
    handleOrganizationSwitchAndExit(null);
    popover.onClose();
    navigate(paths.admin.root);
  }, [navigate, handleOrganizationSwitchAndExit, popover])

  const menuOptions = useMemo(() => {
    const superAdminSwitchedToOrg = user?.role === Role.SUPER_ADMIN && currentOrg;
    const isStaffUser = user?.role === Role.STAFF;
    return [
      {
        label: 'Home',
        linkTo: landingUrl,
      },
      ...superAdminSwitchedToOrg ?
        [
          {
            label: 'Exit Organization',
            onClick: exitOrganizationMode,
          },
        ] :
        [
          {
            label: 'Profile',
            linkTo: paths.user.profile,
          },
        ],
      ...!isStaffUser && user?.role !== Role.SUPER_ADMIN ? [
        {
          label: 'Settings',
          linkTo: paths.setting.root,
        },
      ] : []
    ];
  }, [currentOrg, exitOrganizationMode, user?.role]);

  useEffect(() => {
    async function fetchImage() {
      if (user?.proPicture) {
        const imageBlob: Blob = await getFileById(user.proPicture);
        if (imageBlob) {
          const imageUrl: string = URL.createObjectURL(imageBlob);
          setProPicture(imageUrl);
        } else {
          console.error('Error fetching image:');
        }
      }
    }
    fetchImage();
  }, [user?.proPicture])

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={proPicture ?? ''}
          alt={user?.firstName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {user?.firstName?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {menuOptions.map((option) => (
            <MenuItem key={option.label} onClick={option.onClick ? option.onClick : () => handleClickItem(option.linkTo ?? '')}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </CustomPopover >
    </>
  );
}
